/**
 * Qué papers entran en la fase rápida: de todos los candidatos que traen las fuentes
 * (cientos), se clasifican con Jev solo los mejor puntuados. La puntuación no usa Jev:
 *
 * - Posición en cada lista, combinada con *reciprocal rank fusion*: un paper que sale
 *   arriba en varias listas (varias fuentes u órdenes) sube.
 * - Tipo de estudio según los metadatos (meta-análisis, revisión sistemática, ensayo…),
 *   que es justo lo que más pesa luego en el veredicto.
 * - Citas por año desde la publicación (no solo citas totales, que favorecen lo antiguo).
 */

import { Deduper } from "./dedupe";
import type { Paper } from "./models";

export const RRF_K = 60;

export interface PriorityWeights {
  rank: number;
  design: number;
  citations: number;
}
export const DEFAULT_WEIGHTS: PriorityWeights = { rank: 1, design: 1, citations: 0.5 };

const DESIGN_SIGNALS: [RegExp, number][] = [
  [/meta-?analy|systematic review|umbrella review/i, 1],
  [/randomi[sz]ed controlled trial|randomi[sz]ed trial|\brct\b/i, 0.6],
  [/clinical trial|cohort/i, 0.3],
  [/^review$/i, 0.2],
];

/** Señal de diseño (0-1) a partir del tipo de publicación y del título. */
export function designSignal(p: Paper): number {
  const texts = [...p.publication_types, p.title];
  let best = 0;
  for (const [re, value] of DESIGN_SIGNALS) {
    if (value > best && texts.some((t) => re.test(t))) best = value;
  }
  return best;
}

/** log10(1 + citas por año desde la publicación). */
export function citationSignal(p: Paper, currentYear = new Date().getFullYear()): number {
  const cites = p.cited_by_count ?? 0;
  if (cites <= 0) return 0;
  const age = Math.max(0, currentYear - (p.year ?? currentYear));
  return Math.log10(1 + cites / (age + 1));
}

/**
 * Deduplica los candidatos de todas las listas y devuelve los `limit` mejor puntuados
 * (empates: orden de aparición alternando listas).
 */
export function prioritize(
  lists: Paper[][],
  limit: number,
  weights: PriorityWeights = DEFAULT_WEIGHTS,
  currentYear = new Date().getFullYear(),
): Paper[] {
  const dedup = new Deduper();
  const rrf = new Map<Paper, number>();
  const order: Paper[] = [];
  const longest = Math.max(0, ...lists.map((l) => l.length));
  for (let rank = 0; rank < longest; rank++) {
    for (const list of lists) {
      const p = list[rank];
      if (!p) continue;
      const fresh = dedup.add(p);
      const canonical = fresh ?? dedup.find(p)!;
      if (fresh) order.push(fresh);
      rrf.set(canonical, (rrf.get(canonical) ?? 0) + 1 / (RRF_K + rank + 1));
    }
  }
  const score = (p: Paper) =>
    weights.rank * (rrf.get(p) ?? 0) * RRF_K +
    weights.design * designSignal(p) +
    weights.citations * citationSignal(p, currentYear);
  const scored = order.map((p, i) => ({ p, i, s: score(p) }));
  scored.sort((a, b) => b.s - a.s || a.i - b.i);
  return scored.slice(0, limit).map((x) => x.p);
}
